import {
  SECTION_IDS,
  type MarketingFrame,
  type MarketingViewport,
  type SectionId,
} from "../types";

export interface Vector2Snapshot { x: number; y: number }
export interface Vector3Snapshot { x: number; y: number; z: number }

export interface ScreenRect {
  left: number;
  top: number;
  right: number;
  bottom: number;
}

export interface WorldContinuityGeometry {
  viewport: { width: number; height: number };
  blenderFrame: ScreenRect;
  cubeRadiusPx: number;
  clearancePx: number;
  sectionSpans: Record<SectionId, { startPx: number; endPx: number }>;
  anchors?: Partial<Record<SectionId, Vector2Snapshot>>;
  worldUnitsPerPixel?: number;
  gridStepPx?: number;
}

export interface WorldObjectIdentity { readonly id: string }

export interface WorldObjectIdentities {
  readonly cube: WorldObjectIdentity;
  readonly grid: WorldObjectIdentity;
  readonly camera: WorldObjectIdentity;
  readonly cyanLamp: WorldObjectIdentity;
  readonly orangeLamp: WorldObjectIdentity;
}

export interface CubeSnapshot {
  screen: Vector2Snapshot;
  world: Vector3Snapshot;
  velocity: Vector2Snapshot;
  rotation: Vector3Snapshot;
  silhouette: ScreenRect;
  mode: "jump" | "flight" | "settled";
  focalSection: SectionId;
}

export interface GridSnapshot {
  phasePx: number;
  position: Vector2Snapshot;
  velocity: Vector2Snapshot;
  amplitudePx: number;
  matrix: readonly number[];
}

export interface LampSnapshot {
  phase: number;
  position: Vector2Snapshot;
  intensity: number;
}

export interface WorldSnapshot {
  state: "managed";
  objects: WorldObjectIdentities;
  cube: CubeSnapshot;
  grid: GridSnapshot;
  camera: { position: Vector3Snapshot; target: Vector3Snapshot; fov: number };
  lamps: {
    cyan: LampSnapshot;
    orange: LampSnapshot;
    whiteIntersection: number;
  };
  footerSettle: number;
}

export interface WorldContinuityMount {
  applyWorldSnapshot(snapshot: WorldSnapshot): void;
  resize?(viewport: MarketingViewport, geometry?: WorldContinuityGeometry): void;
  destroy?(): void;
}

export interface WorldContinuityOptions {
  geometry: WorldContinuityGeometry;
  mount?: WorldContinuityMount;
  objects?: WorldObjectIdentities;
}

const TAU = Math.PI * 2;
const FOCAL_DWELL_END = 0.65;
const FIRST_FOCAL_ARRIVAL = 0.25;

const DEFAULT_ANCHOR_RATIOS: readonly Vector2Snapshot[] = [
  { x: 0.68, y: 0.55 }, { x: 0.24, y: 0.32 }, { x: 0.72, y: 0.58 },
  { x: 0.28, y: 0.36 }, { x: 0.50, y: 0.25 }, { x: 0.72, y: 0.61 },
  { x: 0.28, y: 0.36 }, { x: 0.70, y: 0.61 }, { x: 0.26, y: 0.30 },
  { x: 0.50, y: 0.68 }, { x: 0.74, y: 0.30 }, { x: 0.70, y: 0.31 },
  { x: 0.76, y: 0.72 },
];

const clamp = (value: number, minimum = 0, maximum = 1): number =>
  Math.max(minimum, Math.min(maximum, value));

const smoothstep = (value: number): number => {
  const t = clamp(value);
  return t * t * (3 - 2 * t);
};

const lerp = (from: number, to: number, amount: number): number =>
  from + (to - from) * amount;

const lerpPoint = (from: Vector2Snapshot, to: Vector2Snapshot, amount: number): Vector2Snapshot => ({
  x: lerp(from.x, to.x, amount),
  y: lerp(from.y, to.y, amount),
});

const hermite = (
  from: Vector2Snapshot,
  to: Vector2Snapshot,
  fromVelocity: Vector2Snapshot,
  toVelocity: Vector2Snapshot,
  amount: number,
  distance: number,
): Vector2Snapshot => {
  const t = clamp(amount);
  const t2 = t * t;
  const t3 = t2 * t;
  const h00 = 2 * t3 - 3 * t2 + 1;
  const h10 = t3 - 2 * t2 + t;
  const h01 = -2 * t3 + 3 * t2;
  const h11 = t3 - t2;
  return {
    x: h00 * from.x + h10 * distance * fromVelocity.x + h01 * to.x + h11 * distance * toVelocity.x,
    y: h00 * from.y + h10 * distance * fromVelocity.y + h01 * to.y + h11 * distance * toVelocity.y,
  };
};

const objectIdentities = (): WorldObjectIdentities => Object.freeze({
  cube: Object.freeze({ id: "marketing-cube" }),
  grid: Object.freeze({ id: "marketing-grid" }),
  camera: Object.freeze({ id: "marketing-camera" }),
  cyanLamp: Object.freeze({ id: "marketing-lamp-cyan" }),
  orangeLamp: Object.freeze({ id: "marketing-lamp-orange" }),
});

const validateGeometry = (geometry: WorldContinuityGeometry): WorldContinuityGeometry => {
  if (geometry.viewport.width <= 0 || geometry.viewport.height <= 0) {
    throw new RangeError("World viewport dimensions must be positive.");
  }
  if (geometry.cubeRadiusPx <= 0 || geometry.clearancePx < 0) {
    throw new RangeError("Cube radius and frame clearance must be valid.");
  }
  for (const id of SECTION_IDS) {
    const span = geometry.sectionSpans[id];
    if (!span || span.endPx <= span.startPx) {
      throw new RangeError(`World section ${id} must have a positive physical span.`);
    }
  }
  return geometry;
};

const anchorFor = (geometry: WorldContinuityGeometry, id: SectionId): Vector2Snapshot => {
  const supplied = geometry.anchors?.[id];
  if (supplied) return supplied;
  const ratio = DEFAULT_ANCHOR_RATIOS[SECTION_IDS.indexOf(id)] ?? DEFAULT_ANCHOR_RATIOS[0]!;
  return {
    x: ratio.x * geometry.viewport.width,
    y: ratio.y * geometry.viewport.height,
  };
};

const handoffDistance = (frame: MarketingFrame, geometry: WorldContinuityGeometry): number =>
  (frame.handoff - 1) * geometry.viewport.height + frame.travelPx;

const sampleCubePoint = (
  distance: number,
  geometry: WorldContinuityGeometry,
  reducedMotion: boolean,
): Vector2Snapshot => {
  const { blenderFrame, cubeRadiusPx, clearancePx } = geometry;
  const jumpDistance = geometry.viewport.height;
  const start = {
    x: (blenderFrame.left + blenderFrame.right) * 0.5,
    y: blenderFrame.bottom - cubeRadiusPx * 2.25,
  };
  /* Точка, с которой маршрут подхватывает кубик, обязана быть В КАДРЕ.
     Прежняя лежала ниже нижней кромки окна Blender, то есть за экраном: фильм
     отдавал кубик наружу, маршрут забирал его из-за кадра и потом долго вёз
     обратно — зритель всё это время крутил пустое полотно. */
  const seam = {
    x: (blenderFrame.left + blenderFrame.right) * 0.5,
    y: blenderFrame.top + (blenderFrame.bottom - blenderFrame.top) * 0.31,
  };
  const workflow = anchorFor(geometry, "workflow");
  const workflowSpan = geometry.sectionSpans.workflow;
  const firstArrival = Math.max(1, workflowSpan.startPx + (workflowSpan.endPx - workflowSpan.startPx) * FIRST_FOCAL_ARRIVAL);
  const seamVelocity = {
    x: (workflow.x - seam.x) / firstArrival,
    y: (workflow.y - seam.y) / firstArrival,
  };

  if (distance <= 0) {
    const t = clamp((distance + jumpDistance) / jumpDistance);
    const base = hermite(start, seam, { x: 0, y: 0.08 }, seamVelocity, t, jumpDistance);
    const arc = reducedMotion ? 0 : geometry.viewport.height * 0.18 * 16 * t * t * (1 - t) * (1 - t);
    return { x: base.x, y: base.y - arc };
  }

  if (distance < firstArrival) {
    return hermite(seam, workflow, seamVelocity, { x: 0, y: 0 }, distance / firstArrival, firstArrival);
  }

  let sectionIndex = SECTION_IDS.length - 1;
  for (let index = 0; index < SECTION_IDS.length; index += 1) {
    const id = SECTION_IDS[index]!;
    if (distance <= geometry.sectionSpans[id].endPx) {
      sectionIndex = index;
      break;
    }
  }
  const currentId = SECTION_IDS[sectionIndex]!;
  const nextId = SECTION_IDS[Math.min(sectionIndex + 1, SECTION_IDS.length - 1)]!;
  const span = geometry.sectionSpans[currentId];
  const local = clamp((distance - span.startPx) / Math.max(1, span.endPx - span.startPx));
  const departure = reducedMotion ? 0.82 : FOCAL_DWELL_END;
  const travel = smoothstep((local - departure) / Math.max(0.01, 1 - departure));
  if (geometry.viewport.width >= 900 && currentId === "workflow") return workflow;
  if (geometry.viewport.width >= 900 && currentId === "where") {
    // Workflow and Where are one visual block. Leave only in its outro:
    // across the header band, down the gutter, then around the game heading.
    const gutter = anchorFor(geometry, "where");
    const game = anchorFor(geometry, "uses-game");
    const headerBand = Math.max(workflow.y, geometry.viewport.height * 0.26);
    const departurePoint = { x: workflow.x, y: headerBand };
    const above = { x: gutter.x, y: headerBand };
    const beside = { x: game.x, y: gutter.y };
    if (local < 0.10) return workflow;
    if (local < 0.18) return lerpPoint(workflow, departurePoint, smoothstep((local - 0.10) / 0.08));
    if (local < 0.36) return lerpPoint(departurePoint, above, smoothstep((local - 0.18) / 0.18));
    if (local < 0.68) return lerpPoint(above, gutter, smoothstep((local - 0.36) / 0.32));
    if (local < 0.82) return lerpPoint(gutter, beside, smoothstep((local - 0.68) / 0.14));
    return lerpPoint(beside, game, smoothstep((local - 0.82) / 0.18));
  }
  return lerpPoint(anchorFor(geometry, currentId), anchorFor(geometry, nextId), travel);
};

const derivativeAt = (
  sampler: (distance: number) => Vector2Snapshot,
  distance: number,
): Vector2Snapshot => {
  const epsilon = 0.001;
  const before = sampler(distance - epsilon);
  const after = sampler(distance + epsilon);
  return {
    x: Number(((after.x - before.x) / (epsilon * 2)).toFixed(12)),
    y: Number(((after.y - before.y) / (epsilon * 2)).toFixed(12)),
  };
};

const footerSettleFor = (frame: MarketingFrame): number =>
  smoothstep((frame.sections.footer.progress - 0.42) / 0.58);

const gridPositionAt = (
  distance: number,
  geometry: WorldContinuityGeometry,
  footerSettle: number,
): { position: Vector2Snapshot; amplitude: number } => {
  const height = geometry.viewport.height;
  const activation = smoothstep((distance - height * 0.18) / (height * 0.52));
  const amplitude = (geometry.gridStepPx ?? 64) * 0.22 * activation * (1 - footerSettle * 0.8);
  return {
    position: {
      x: distance * 0.055 + Math.sin(distance * 0.0041) * amplitude,
      y: distance * 0.082 + Math.sin(distance * 0.0027 + 0.9) * amplitude * 0.7,
    },
    amplitude,
  };
};

const sampleGrid = (
  distance: number,
  geometry: WorldContinuityGeometry,
  footerSettle: number,
): GridSnapshot => {
  const sample = (at: number): Vector2Snapshot => gridPositionAt(at, geometry, footerSettle).position;
  const { position, amplitude } = gridPositionAt(distance, geometry, footerSettle);
  const velocity = derivativeAt(sample, distance);
  return {
    phasePx: distance,
    position,
    velocity,
    amplitudePx: amplitude,
    matrix: Object.freeze([
      1, 0, 0, 0,
      0, 1, 0, 0,
      0, 0, 1, 0,
      position.x, 0, position.y, 1,
    ]),
  };
};

const wrapRadians = (value: number): number => {
  const wrapped = value % TAU;
  return wrapped < 0 ? wrapped + TAU : wrapped;
};

const sampleLamps = (now: number, geometry: WorldContinuityGeometry): WorldSnapshot["lamps"] => {
  const time = Number.isFinite(now) ? now : 0;
  const cyanPhase = wrapRadians(time / 48_000 * TAU);
  const orangePhase = wrapRadians(time / 61_000 * TAU + Math.PI);
  const width = geometry.viewport.width;
  const height = geometry.viewport.height;
  const cyan = {
    phase: cyanPhase,
    position: {
      x: width * 0.14 + Math.sin(cyanPhase) * width * 0.025,
      y: height * 0.55 + Math.cos(cyanPhase * 0.71) * height * 0.018,
    },
    intensity: 0.65 + Math.sin(cyanPhase * 0.53) * 0.04,
  };
  const orange = {
    phase: orangePhase,
    position: {
      x: width * 0.86 + Math.sin(orangePhase) * width * 0.025,
      y: height * 0.53 + Math.cos(orangePhase) * height * 0.019,
    },
    intensity: 0.61 + Math.cos(orangePhase * 0.47) * 0.035,
  };
  const separation = Math.hypot(cyan.position.x - orange.position.x, cyan.position.y - orange.position.y);
  return {
    cyan,
    orange,
    whiteIntersection: clamp(1 - separation / (width * 0.24)) * 0.46,
  };
};

export class WorldContinuityController {
  private geometry: WorldContinuityGeometry;
  private readonly mount?: WorldContinuityMount;
  private readonly identities: WorldObjectIdentities;

  constructor(options: WorldContinuityOptions) {
    this.geometry = validateGeometry(options.geometry);
    this.mount = options.mount;
    this.identities = options.objects ?? objectIdentities();
  }

  render(frame: MarketingFrame, now: number): WorldSnapshot {
    const geometry = this.geometry;
    const distance = handoffDistance(frame, geometry);
    const reducedMotion = frame.motion === "reduced";
    const cubeSampler = (at: number): Vector2Snapshot => sampleCubePoint(at, geometry, reducedMotion);
    const screen = cubeSampler(distance);
    const velocity = derivativeAt(cubeSampler, distance);
    const footerSettle = footerSettleFor(frame);
    const worldUnitsPerPixel = geometry.worldUnitsPerPixel ?? 0.0025;
    const world = {
      x: (screen.x - geometry.viewport.width * 0.5) * worldUnitsPerPixel,
      y: 0.174,
      z: (screen.y - geometry.viewport.height * 0.5) * worldUnitsPerPixel,
    };
    const focalSection = frame.activeSection;
    const cube: CubeSnapshot = {
      screen,
      world,
      velocity,
      /*
       * Кубик на маркетинговой части ЛЕВИТИРУЕТ, а не катится. Поворот вокруг X
       * был привязан к пройденному расстоянию прокрутки с коэффициентом 0.006 —
       * на десяти тысячах пикселей это девять с половиной полных оборотов, и
       * кубик выглядел трясущимся волчком. Теперь прокрутка даёт ограниченный
       * наклон, как у предмета, который несёт поток, а не катит по полу.
       */
      rotation: {
        x: Math.sin(distance * 0.00028) * 0.2 * (1 - footerSettle),
        y: Math.atan2(velocity.y, velocity.x) * 0.09 * (1 - footerSettle),
        z: clamp(velocity.x * -0.09, -0.13, 0.13) * (1 - footerSettle),
      },
      silhouette: {
        left: screen.x - geometry.cubeRadiusPx,
        top: screen.y - geometry.cubeRadiusPx,
        right: screen.x + geometry.cubeRadiusPx,
        bottom: screen.y + geometry.cubeRadiusPx,
      },
      mode: distance < 0 ? "jump" : footerSettle > 0.72 ? "settled" : "flight",
      focalSection,
    };
    const grid = sampleGrid(distance, geometry, footerSettle);
    const camera = {
      position: {
        x: world.x * 0.16,
        y: lerp(6.4, 6.05, footerSettle),
        z: world.z * 0.12 + grid.phasePx * worldUnitsPerPixel * 0.08,
      },
      target: { x: world.x * 0.08, y: 0, z: world.z * 0.08 },
      fov: lerp(35, 33.5, footerSettle),
    };
    const snapshot: WorldSnapshot = {
      state: "managed",
      objects: this.identities,
      cube,
      grid,
      camera,
      lamps: sampleLamps(now, geometry),
      footerSettle,
    };
    this.mount?.applyWorldSnapshot(snapshot);
    return snapshot;
  }

  resize(viewport: MarketingViewport, geometry?: WorldContinuityGeometry): void {
    if (geometry) this.geometry = validateGeometry(geometry);
    this.mount?.resize?.(viewport, geometry);
  }

  destroy(): void {
    this.mount?.destroy?.();
  }
}
